import { Text, Icon, Tooltip } from "@src/straps/base";
import * as React from "react";
import Icons from "../../../icons";
import {
  OnboardingStatusEnum,
  OnboardingStepType,
  OnboardingSubStep,
} from "./completenessSchema";
import { isOptedOutStepOmittedFromCount } from "./utils";

const tooltipText = {
  [OnboardingStatusEnum.Done]: "Completed",
  [OnboardingStatusEnum.Processing]: "Processing",
  [OnboardingStatusEnum.Open]: "Not completed",
  [OnboardingStatusEnum.Done_NA]: "Not Available",
  [OnboardingStatusEnum.OptOut]: "Opted out",
  [OnboardingStatusEnum.N_A]: "Not applicable",
} as const;

export const StatusTooltip = ({
  content,
  children,
}: {
  readonly content: React.ReactNode;
  readonly children: React.ComponentProps<typeof Tooltip>["children"];
}) => {
  return (
    <Tooltip
      fixed
      align="right"
      content={<Text variant="sa_t-10-700">{content}</Text>}
    >
      {children}
    </Tooltip>
  );
};

export default function OnboardingStatusIcon({
  step,
  incompletable,
  informational,
}: {
  readonly step: OnboardingStepType | OnboardingSubStep;
  readonly incompletable?: boolean;
  readonly informational?: boolean;
}) {
  return (
    <StatusTooltip
      content={
        <>
          {incompletable && "Add Details"}
          {informational && "Informational"}
          {!informational && !incompletable && tooltipText[step.status]}
        </>
      }
    >
      <span className="inline-flex items-center">
        <StatusIcon step={step} />
      </span>
    </StatusTooltip>
  );
}

function StatusIcon({
  step,
}: {
  readonly step: OnboardingStepType | OnboardingSubStep;
}) {
  if (step.status === OnboardingStatusEnum.Done_NA)
    return <Icons icon="Ban" className="color--red h-3 w-3" />;

  if (isOptedOutStepOmittedFromCount(step))
    return <Icon name="opt-out" size="small" color="secondary" />;

  if (step.countable && step.complete)
    return (
      <Icons
        icon="Check"
        className="color--charcoal h-2.5 w-2.5"
        aria-label="step completed"
      />
    );

  if (step.status === OnboardingStatusEnum.Processing)
    return (
      <Icons
        icon="BackpackLogoNegative"
        className="color--blue-gray h-4 w-3"
        aria-label="step processing"
      />
    );

  const incompletable =
    step.stepType !== "subStep" ? step.incompletable : false;
  const informational =
    step.stepType !== "subStep" ? step.informational : false;
  const readOnly = step.readOnly;

  if (informational) {
    return <Icon name="info" size="small" color="secondary" />;
  }

  if (incompletable && !readOnly) {
    return (
      <Icon
        name="caret-right"
        color="secondary"
        size="small"
        className="!w-1.5"
        aria-label="jump to step"
      />
    );
  }

  if (step.status === OnboardingStatusEnum.Open && !readOnly)
    return (
      <Icon
        name="caret-right"
        color="accent-1"
        size="small"
        className="!w-1.5"
        aria-label="jump to step"
      />
    );

  return null;
}
